import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useState, useEffect, useCallback, useMemo, } from 'react';
import classnames from 'classnames';
import { ETooltipPlacement, ETooltipAction, ETooltipAlign, TextField, EInputTextType, InfoTooltip, FilterWrapper, } from 'Shared/components';
import { EKeyCode } from 'Shared/consts';
import validatePValue from './utils/validatePValue/validatePValue';
import convertPValueToString from './utils/convertPValueToString/convertPValueToString';
import convertPValueToNumber from './utils/convertPValueToNumber/convertPValueToNumber';
import styles from './FilterByPValue.css';
import ValidationWarning from './components/ValidationWarning/ValidationWarning';
const title = (_jsxs("span", { children: ["Expected p-value format is", ' ', _jsx("strong", { children: "4.4e-3" }, void 0), ' ', "or", ' ', _jsx("strong", { children: "0.0044" }, void 0), ' ', "(0 \u2264 p-value \u2264 1)"] }, void 0));
const FilterByPValue = (props) => {
    const { value, placeholder, className, onChange, validityCallback, disabled, label, inputClassName = '', } = props;
    // Need to store PValue in string format because user can use both numeric and scientific formats
    const [PValue, setPValue] = useState(convertPValueToString(value));
    const [isValid, setIsValid] = useState(true);
    useEffect(() => {
        setPValue((currentValue) => {
            if (convertPValueToNumber(currentValue) === value) {
                return currentValue;
            }
            return convertPValueToString(value);
        });
    }, [value]);
    const handleChange = useCallback((event) => {
        const element = event.target;
        const { value: updatedValue } = element;
        const isUpdatedValueValid = validatePValue(updatedValue);
        setPValue(updatedValue);
        setIsValid(isUpdatedValueValid);
        if (validityCallback) {
            validityCallback(isUpdatedValueValid);
        }
    }, [validityCallback]);
    const updatePValue = useCallback(() => {
        if (isValid) {
            const updatedPValue = convertPValueToNumber(PValue);
            if (updatedPValue !== value && onChange) {
                onChange(updatedPValue);
            }
        }
    }, [PValue, isValid, onChange, value]);
    const handleKeyDown = useCallback((event) => {
        const { code } = event;
        switch (code) {
            case EKeyCode.Enter:
            case EKeyCode.NumpadEnter:
                updatePValue();
                break;
            default:
        }
    }, [updatePValue]);
    const id = 'gvPValueControl';
    const infoClassNames = classnames(styles.info, {
        [styles.invalidFormatTooltip]: !isValid,
    });
    const inputClassNames = useMemo(() => (classnames(styles.input, inputClassName, {
        [styles.hasError]: !isValid,
    })), [inputClassName, isValid]);
    return (_jsxs(FilterWrapper, Object.assign({ id: id, className: className, label: label, dataTestId: "pvalue-text-field" }, { children: [!isValid && (_jsx(ValidationWarning, {}, void 0)), _jsx(TextField, { id: id, type: EInputTextType.Text, value: PValue, placeholder: placeholder, className: inputClassNames, onChange: handleChange, onKeyDown: handleKeyDown, onBlur: updatePValue, disabled: disabled, "aria-label": placeholder }, void 0), _jsx("span", Object.assign({ className: infoClassNames }, { children: _jsx(InfoTooltip, Object.assign({ targetEvent: ETooltipAction.Hover, position: ETooltipPlacement.BottomCenter, id: "pValue", overlayClassName: styles.tooltip, align: ETooltipAlign.Start }, { children: title }), void 0) }), void 0)] }), void 0));
};
export default memo(FilterByPValue);
